import { useState, useEffect } from "react"
import { FormModal } from "./FormModal"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useAuth } from "@/hooks/useAuth"
import { useUsers } from "@/hooks/use-users"
import type { FirebaseUser, UserRole } from "@/lib/firebase/types"

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: FirebaseUser | null;
  onSuccess?: () => void;
}

type FormDataType = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  role: UserRole;
}

export function UserModal({ isOpen, onClose, user, onSuccess }: UserModalProps) {
  const { user: currentUser } = useAuth();
  const { createUser, updateUser, isLoading } = useUsers();
  const [formData, setFormData] = useState<FormDataType>({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    role: "user"
  });

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      if (user) {
        // Editing existing user
        setFormData({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          username: user.username || "",
          password: "", // Empty for editing
          role: user.role || "user"
        });
      } else {
        // Creating new user
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          username: "",
          password: "",
          role: "user"
        });
      }
    }
  }, [isOpen, user]);

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) return false;
    if (!formData.lastName.trim()) return false;
    if (!formData.email.trim()) return false;
    if (!formData.username.trim()) return false;
    if (!user && !formData.password.trim()) return false;
    if (!formData.role) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        username: formData.username,
        password: formData.password,
        role: formData.role,
        name: `${formData.firstName} ${formData.lastName}`.trim()
      };

      if (user?.uid) {
        await updateUser(user.uid, userData);
      } else {
        await createUser(userData);
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting user:', error);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? "Edit User" : "Create User"}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            required
            className="h-8"
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            required
            className="h-8"
          />
        </div>
        <div>
          <Label htmlFor="username">Username *</Label>
          <Input
            id="username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
            className="h-8"
          />
        </div>
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="h-8"
          />
        </div>
        <div>
          <Label htmlFor="password">{user ? "Password (Optional)" : "Password *"}</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!user}
            placeholder={user ? "Leave blank to keep current password" : ""}
            className="h-8"
          />
        </div>
        <div>
          <Label htmlFor="role">Role *</Label>
          <Select
            value={formData.role}
            onValueChange={(value: UserRole) => setFormData({ ...formData, role: value })}
          >
            <SelectTrigger id="role" className="h-8">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="editor">Editor</SelectItem>
              {currentUser?.role === 'admin' && (
                <SelectItem value="admin">Admin</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    </FormModal>
  );
} 